import { supabase } from '../lib/supabase';
import { OrdemDeServico, Status, Pedido, DetalhesDoPedido, AjudantesOS } from '../types/ordemServico';
import { sanitizeDeep } from '../utils/textSanitizer';
import { propostaMetaService } from './propostaMetaService';

export const ordemServicoService = {
  async getAll(options?: { maxRecords?: number; step?: number }): Promise<OrdemDeServico[]> {
    let allOS: OrdemDeServico[] = [];
    let from = 0;
    const step = options?.step || 1000;
    const maxRecords = options?.maxRecords || 10000; // Limite de segurança para evitar loops infinitos
    
    while (from < maxRecords) {
      let { data, error } = await supabase
        .from('ordem_de_servico')
        .select('*')
        .order('id', { ascending: false })
        .range(from, from + step - 1);

      if (error) {
        const retry = await supabase
          .from('ordem_de_servico')
          .select('*')
          .range(from, from + step - 1);
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        console.warn(`[getAll OS] Warning fetching page at offset ${from}:`, error.message);
        break;
      }
      
      if (data && data.length > 0) {
        allOS = [...allOS, ...data];
        if (data.length < step) {
          break;
        }
        from += step;
      } else {
        break;
      }
    }
    return sanitizeDeep(allOS);
  },

  async getById(id: number): Promise<OrdemDeServico | null> {
    const { data, error } = await supabase
      .from('ordem_de_servico')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return sanitizeDeep(data);
  },

  async create(os: Omit<OrdemDeServico, 'id'>): Promise<OrdemDeServico> {
    const { data, error } = await supabase
      .from('ordem_de_servico')
      .insert([os])
      .select()
      .single();

    if (error) throw error;
    return sanitizeDeep(data);
  },

  async update(id: number, os: Partial<OrdemDeServico>): Promise<OrdemDeServico> {
    const { data, error } = await supabase
      .from('ordem_de_servico')
      .update(os)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return sanitizeDeep(data);
  },

  async delete(id: number): Promise<void> {
    const { error } = await supabase
      .from('ordem_de_servico')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getStatus(): Promise<Status[]> {
    const { data, error } = await supabase.from('status').select('*');
    if (error) return [];
    return sanitizeDeep(data || []);
  },

  async getAjudantesOS(osId: number): Promise<AjudantesOS[]> {
    const { data, error } = await supabase
      .from('ajudantes_os')
      .select('*')
      .eq('ordem_de_servico_id', osId);

    if (error) return [];
    return sanitizeDeep(data || []);
  },

  async getDetalhesPedido(pedidoId: number): Promise<DetalhesDoPedido[]> {
    const { data, error } = await supabase
      .from('detalhes_do_pedido')
      .select('*')
      .eq('pedido_id', pedidoId);

    if (error) return [];
    const { items } = propostaMetaService.separateItemsAndMeta(data || []);
    return sanitizeDeep(items || []);
  }
};

